import type {Mat4Array} from "@AppBuilderLib/entities/parameter/config/common";
import type {IParameterStore} from "@AppBuilderLib/entities/parameter/config/shapediverStoreParameters";
import {
	ParameterValueDefinition,
	useResolveParameterValues,
} from "@AppBuilderLib/entities/parameter/model/useResolveParameterValues";
import {useShapeDiverStoreParameters} from "@AppBuilderLib/entities/parameter/model/useShapeDiverStoreParameters";
import {IUseSessionDto} from "@AppBuilderLib/entities/session/model/useSession";
import {useShapeDiverStoreSession} from "@AppBuilderLib/entities/session/model/useShapeDiverStoreSession";
import {IProcessDefinition} from "@AppBuilderLib/shared/config/shapediverStoreProcessManager";
import {useShapeDiverStoreProcessManager} from "@AppBuilderLib/shared/model/useShapeDiverStoreProcessManager";
import {ResOutput, ResOutputContent} from "@shapediver/sdk.geometry-api-sdk-v2";
import {
	ISessionApi,
	ITreeNode,
	SessionData,
	SessionOutputData,
	TreeNode,
} from "@shapediver/viewer.session";
import {GlobalAccessObjects} from "@shapediver/viewer.shared.global-access-objects";
import {mat4} from "gl-matrix";
import {
	MutableRefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import {useShallow} from "zustand/react/shallow";
import {
	IAppBuilder,
	IAppBuilderInstanceDefinition,
	IAppBuilderSettingsSession,
} from "../config/appbuilder";
import {useShapeDiverStoreInstances} from "./useShapeDiverStoreInstances";

import {useSessions} from "@AppBuilderLib/entities/session/model/useSessions";
import {Logger} from "@AppBuilderLib/shared/lib/logger";
import {useShapeDiverStoreViewportAccessFunctions} from "@AppBuilderShared/entities/viewport/model/useShapeDiverStoreViewportAccessFunctions";
import useResolveAppBuilderSessions from "./useResolveAppBuilderSessions";
interface Props {
	namespace: string;
	/**
	 * The session API of the controller session.
	 * This is used to add the instances.
	 */
	sessionApi: ISessionApi | undefined;
	/**
	 * The app builder data to parse.
	 */
	appBuilderData: IAppBuilder | undefined;
	/**
	 * The process ID to add the promises to.
	 */
	processManagerId: string | undefined;
	/**
	 * Called once the instance pipeline has attached its own tracked work
	 * to the handed-off process manager, or once it is clear that no
	 * instance process will be spawned for that manager.
	 */
	onProcessManagerAttached?: (processManagerId: string | undefined) => void;
}

type IParsedInstanceDefinition = {
	session: ISessionApi;
	parameterValues: {[key: string]: string};
	transformations?: number[][];
	originalIndex: number;
	name?: string;
	outputActions: IAppBuilderInstanceDefinition["outputActions"];
};

/**
 * Hook for creating app builder instances.
 * This hook parses the app builder data and creates instances in the viewer.
 * The instances are created with the correct parameter set and transformations.
 *
 * @param props
 */
export function useAppBuilderInstances(props: Props) {
	const {
		namespace,
		sessionApi,
		appBuilderData,
		processManagerId: sessionProcessManagerId,
		onProcessManagerAttached,
	} = props;

	const {
		sessions,
		pendingSessions,
		addSessionUpdateCallback,
		createPendingSession,
	} = useShapeDiverStoreSession();
	const {addProcess, createProcessManager} =
		useShapeDiverStoreProcessManager();
	const {
		addCustomizationResult,
		removeCustomizationResult,
		customizationResults,
		addInstance,
		removeInstance,
	} = useShapeDiverStoreInstances();

	const {batchParameterValueUpdate, getParameter} =
		useShapeDiverStoreParameters(
			useShallow((state) => ({
				batchParameterValueUpdate: state.batchParameterValueUpdate,
				getParameter: state.getParameter,
			})),
		);

	const [instanceNodes, setInstanceNodes] = useState<{
		[key: string]: ITreeNode;
	}>({});

	const [sessionNodeVersion, setSessionNodeVersion] = useState(0);

	const firstLoadDoneRef = useRef(false);
	const customizationResultInStoreRef = useRef(customizationResults);
	const instanceNodesRef = useRef<{
		[key: string]: ITreeNode;
	}>({});
	const instancesRef = useRef<IAppBuilderInstanceDefinition[]>([]);
	const sessionsRef = useRef(sessions);
	const pendingSessionsRef = useRef(pendingSessions);
	const sessionNodeRef = useRef<ITreeNode | undefined>(undefined);
	const sessionProcessManagerIdRef = useRef(sessionProcessManagerId);
	const pendingOutputActionRequestKeyRef = useRef<string | undefined>(
		undefined,
	);
	const pendingOutputActionPromiseRef = useRef<Promise<void> | undefined>(
		undefined,
	);
	const speculativeProcessCounterRef = useRef(0);
	const speculativeProcessResolversRef = useRef<{
		[key: number]: () => void;
	}>({});
	const pendingParsingProcessTokenRef = useRef<number | undefined>(undefined);
	const parsingProcessTokensByParsedRevisionRef = useRef<{
		[key: number]: number;
	}>({});
	const sessionUpdateProcessTokensByVersionRef = useRef<{
		[key: number]: number;
	}>({});
	const parsedAppBuilderInstancesKeyRef = useRef("");
	const parsedAppBuilderInstancesRevisionRef = useRef(0);

	useEffect(() => {
		sessionProcessManagerIdRef.current = sessionProcessManagerId;
	}, [sessionProcessManagerId]);

	useEffect(() => {
		customizationResultInStoreRef.current = customizationResults;
	}, [customizationResults]);

	useEffect(() => {
		sessionsRef.current = sessions;
	}, [sessions]);

	useEffect(() => {
		pendingSessionsRef.current = pendingSessions;
	}, [pendingSessions]);

	const resolveSpeculativeProcess = useCallback((token?: number) => {
		if (token === undefined) return;

		const resolve = speculativeProcessResolversRef.current[token];
		if (!resolve) return;

		delete speculativeProcessResolversRef.current[token];
		resolve();
	}, []);

	const createSpeculativeProcessManager = useCallback(
		(name: string) => {
			if (!sessionApi) return undefined;

			const token = ++speculativeProcessCounterRef.current;
			let resolvePromise: () => void;
			const promise = new Promise<void>((resolve) => {
				resolvePromise = resolve;
			});
			const processManagerId = createProcessManager(sessionApi.id);
			addProcess(processManagerId, {
				name,
				promise,
			});

			speculativeProcessResolversRef.current[token] = resolvePromise!;

			return token;
		},
		[addProcess, createProcessManager, sessionApi],
	);

	const parsedInstancesToKey = useCallback(
		(parsedInstances: IParsedInstanceDefinition[]) =>
			JSON.stringify(
				parsedInstances.map((instance) => ({
					sessionId: instance.session.id,
					parameterValues: instance.parameterValues,
					transformations: instance.transformations,
					originalIndex: instance.originalIndex,
					name: instance.name,
					outputActions: instance.outputActions,
				})),
			),
		[],
	);

	// store for the parsed app builder instances
	const [parsedAppBuilderInstances, setParsedAppBuilderInstances] = useState<
		IParsedInstanceDefinition[]
	>([]);
	const [
		parsedAppBuilderInstancesRevision,
		setParsedAppBuilderInstancesRevision,
	] = useState(0);
	const [parameterValuesData, setParameterValuesData] = useState<{
		parameterValues: ParameterValueDefinition[];
		information: {
			parameterValuesMap: {
				[key: string]: {
					[key: string]: number;
				};
			};
			instances?: IAppBuilderInstanceDefinition[];
			parseProcessToken?: number;
		};
	}>();

	const {instances, embeddedSessions} = useMemo(() => {
		if (!appBuilderData) {
			setParsedAppBuilderInstances([]);
			return {
				instances: undefined,
				embeddedSessions: [],
			};
		}

		const sessionsDescriptions: {slug: string; id: string}[] = [];
		appBuilderData.instances?.forEach((instance) => {
			// check if the session is already added
			// either in the currently loaded sessions, or in the pending sessions
			if (
				!sessionsRef.current[instance.sessionId] &&
				!Object.values(pendingSessionsRef.current).find(
					(session) => session.dto.id === instance.sessionId,
				)
			) {
				// use slug if available, otherwise use sessionId
				const slug = instance.slug || instance.sessionId;

				const index = sessionsDescriptions.findIndex(
					(session) => session.slug === slug,
				);

				// there already is a session with this slug
				// check if the ids match, if not, warn the user
				if (index !== -1) {
					if (sessionsDescriptions[index].id !== instance.sessionId) {
						Logger.warn(
							`Multiple instances are using the same slug "${slug}" but different session ids ("${sessionsDescriptions[index].id}" and "${instance.sessionId}"). This can lead to unexpected behavior.`,
						);
					}
					return;
				}

				sessionsDescriptions.push({
					slug,
					id: instance.sessionId,
				});
			}
		});

		return {
			instances: appBuilderData.instances,
			embeddedSessions: sessionsDescriptions as (IUseSessionDto &
				IAppBuilderSettingsSession)[],
		};
	}, [appBuilderData]);

	// from the incoming slugs, retrieve the session data from the platform
	const {sessions: sessionData, error: platformError} =
		useResolveAppBuilderSessions(embeddedSessions);

	// add some necessary flags to the resolved sessions
	const resolvedSessions = useMemo(() => {
		if (!sessionData) return [];
		(
			sessionData as (IUseSessionDto & IAppBuilderSettingsSession)[]
		).forEach((s) => {
			s.loadOutputs = false;
			s.registerParametersAndExports = false;
			s.keepInStore = true;
		});
		return sessionData;
	}, [sessionData]);

	// create the sessions
	const {errors: sessionErrors} = useSessions(resolvedSessions ?? []);

	useEffect(() => {
		if (platformError)
			Logger.warn("Error resolving sessions:", platformError);

		for (const sessionError of sessionErrors)
			Logger.warn("Error creating sessions:", sessionError);
	}, [platformError, sessionErrors]);

	const createParsedInstances = useCallback(
		(
			instances: IAppBuilderInstanceDefinition[],
			parseProcessToken?: number,
		) => {
			const parsedInstances: IParsedInstanceDefinition[] = [];

			instances.forEach((instance, index) => {
				const session = sessionsRef.current[instance.sessionId];
				if (!session) return;

				const parameterValuesWithIds: {[key: string]: string} = {};

				Object.entries(instance.parameterValues ?? {}).map(
					([key, value]) => {
						const parameter = Object.values(
							session.parameters,
						).find(
							(p) =>
								p.displayname === key ||
								p.id === key ||
								p.name === key,
						);
						if (parameter) {
							parameterValuesWithIds[parameter.id] = value + "";
						} else {
							Logger.warn(
								`Could not find parameter for key ${key} in session ${session.id}.`,
							);
						}
					},
				);

				parsedInstances.push({
					session,
					parameterValues: parameterValuesWithIds,
					transformations: instance.transformations,
					originalIndex: index,
					name: instance.name,
					outputActions: instance.outputActions,
				});
			});
			const nextKey = parsedInstancesToKey(parsedInstances);
			if (nextKey === parsedAppBuilderInstancesKeyRef.current) {
				if (pendingParsingProcessTokenRef.current === parseProcessToken)
					pendingParsingProcessTokenRef.current = undefined;
				resolveSpeculativeProcess(parseProcessToken);
				onProcessManagerAttached?.(sessionProcessManagerIdRef.current);
				return;
			}

			parsedAppBuilderInstancesKeyRef.current = nextKey;
			const nextRevision =
				parsedAppBuilderInstancesRevisionRef.current + 1;
			parsedAppBuilderInstancesRevisionRef.current = nextRevision;
			if (parseProcessToken !== undefined) {
				parsingProcessTokensByParsedRevisionRef.current[nextRevision] =
					parseProcessToken;
			}
			if (pendingParsingProcessTokenRef.current === parseProcessToken)
				pendingParsingProcessTokenRef.current = undefined;
			setParsedAppBuilderInstances(parsedInstances);
			setParsedAppBuilderInstancesRevision(nextRevision);
		},
		[
			onProcessManagerAttached,
			parsedInstancesToKey,
			resolveSpeculativeProcess,
		],
	);

	// Use useResolvedParameters to resolve the parameter values
	const {values: resolvedParameterValuesArray} = useResolveParameterValues({
		namespace,
		parameterValues: parameterValuesData?.parameterValues,
	});

	// once the pending sources are loaded, we can create the instances
	useEffect(() => {
		if (!parameterValuesData || !resolvedParameterValuesArray) return;
		const {parameterValuesMap, instances, parseProcessToken} =
			parameterValuesData.information;
		if (!instances) {
			resolveSpeculativeProcess(parseProcessToken);
			return;
		}

		const instancesCopy = JSON.parse(
			JSON.stringify(instances),
		) as IAppBuilderInstanceDefinition[];
		Object.entries(parameterValuesMap ?? {}).forEach(
			([instanceIndex, instanceSourceMap]) => {
				Object.entries(instanceSourceMap).forEach(([key, value]) => {
					if (
						parameterValuesMap[instanceIndex] !== undefined &&
						parameterValuesMap[instanceIndex][key] !== undefined
					) {
						const resolvedValue =
							resolvedParameterValuesArray[value];

						const instance = instancesCopy[parseInt(instanceIndex)];

						if (!instance.parameterValues)
							instance.parameterValues = {};

						if (resolvedValue !== undefined) {
							instance.parameterValues[key] = resolvedValue + "";
						} else {
							instance.parameterValues[key] = "";
							Logger.warn(
								`Could not resolve parameter value source for parameter ${key} in instance ${instance.name}. Setting value to empty string.`,
							);
						}
					}
				});
			},
		);

		createParsedInstances(instancesCopy, parseProcessToken);
		setParameterValuesData(undefined);
	}, [
		createParsedInstances,
		resolvedParameterValuesArray,
		parameterValuesData,
		resolveSpeculativeProcess,
	]);

	useEffect(() => {
		if (!instances) return;

		instances.forEach((instance) => {
			const session = sessionsRef.current[instance.sessionId];

			if (!session)
				// check if there are sessions that were not created yet
				createPendingSession(instance.sessionId);
		});
	}, [instances]);

	/**
	 * Parse the app builder data.
	 * Gather all the necessary information to create the instances.
	 */
	useEffect(() => {
		if (!instances) {
			onProcessManagerAttached?.(sessionProcessManagerIdRef.current);
			return;
		}

		// don't do anything if one of the sessions is not available yet.
		// If this render came from an AppBuilder output callback, keep that
		// bridge PM alive until the sessions arrive and parsing can continue.
		const missingSession = instances.some(
			(instance) => !sessions[instance.sessionId],
		);
		if (missingSession) return;

		// check if the instances have changed
		if (
			JSON.stringify(instances) === JSON.stringify(instancesRef.current)
		) {
			// Nothing downstream will be spawned for the current AppBuilder PM.
			onProcessManagerAttached?.(sessionProcessManagerIdRef.current);
			return;
		}

		const parseProcessToken = createSpeculativeProcessManager(
			"AppBuilder Instance Parse Bridge",
		);
		const previousParseProcessToken = pendingParsingProcessTokenRef.current;
		pendingParsingProcessTokenRef.current = parseProcessToken;
		resolveSpeculativeProcess(previousParseProcessToken);

		// set the instances ref to the current instances
		instancesRef.current = JSON.parse(JSON.stringify(instances));

		const existingNames = new Set<string>();
		const parameterValuesMap: {
			[key: string]: {[key: string]: number};
		} = {};
		const parameterValuesToLoad: ParameterValueDefinition[] = [];

		instances.forEach((instance, index) => {
			if (instance.name) {
				if (existingNames.has(instance.name)) {
					Logger.warn(
						`Duplicate instance name found: ${instance.name}. Instance names must be unique, skipping instance.`,
					);
					return;
				} else {
					existingNames.add(instance.name);
				}
			}

			Object.entries(instance.parameterValues ?? {}).forEach(
				([key, value]) => {
					const parameterIndex =
						parameterValuesToLoad.push({
							id: key,
							value: value,
							namespace: instance.sessionId,
						}) - 1;

					if (!parameterValuesMap[index])
						parameterValuesMap[index] = {};

					parameterValuesMap[index][key] = parameterIndex;
				},
			);
		});

		setParameterValuesData({
			parameterValues: parameterValuesToLoad,
			information: {
				parameterValuesMap,
				instances,
				parseProcessToken,
			},
		});
	}, [
		createSpeculativeProcessManager,
		instances,
		onProcessManagerAttached,
		resolveSpeculativeProcess,
		sessions,
	]);

	const sessionUpdateCallback = useCallback(
		(newNode?: ITreeNode, oldNode?: ITreeNode) => {
			sessionNodeRef.current = newNode;
			const replaced = !!(oldNode && newNode && oldNode !== newNode);
			// If the session node was replaced (old → new), bump the version
			// to force a re-run that rebuilds instances with current parameter
			// values. Only fire when oldNode exists (node was replaced, not created)
			// and newNode differs (avoid no-op callbacks).
			if (replaced) {
				const sessionUpdateProcessToken =
					createSpeculativeProcessManager(
						"AppBuilder Session Update Bridge",
					);
				setSessionNodeVersion((v) => {
					const nextVersion = v + 1;
					if (sessionUpdateProcessToken !== undefined) {
						sessionUpdateProcessTokensByVersionRef.current[
							nextVersion
						] = sessionUpdateProcessToken;
					}
					return nextVersion;
				});
				// Don't re-add old instances to the replaced node — they have
				// stale geometry from the previous parameter set. A new cycle
				// is starting that will build fresh instances via
				// addInstanceToSceneTree. Re-adding old instances here would
				// make their stale geometry visible as intermediate results.
				return;
			}
			if (!newNode) return;

			Object.values(instanceNodesRef.current).forEach((instance) => {
				if (newNode.hasChild(instance)) return;
				// add the instance to the controller session node
				newNode.addChild(instance);
				// update the version of the node
				// this won't be triggered as long as a process is running
				newNode.updateVersion();
			});
		},
		[createSpeculativeProcessManager],
	);

	useEffect(() => {
		for (const instanceId in instanceNodes) {
			addInstance(instanceId, instanceNodes[instanceId]);
		}

		// Only update the ref when we have actual instances.
		// When instanceNodes is cleared to {} (during effect cleanup for a
		// new cycle), keep the ref pointing to the last successfully built
		// instances so addInstanceToSceneTree can remove them from the node,
		// and sessionUpdateCallback can re-add them on non-replacement updates.
		if (Object.keys(instanceNodes).length > 0) {
			instanceNodesRef.current = instanceNodes;
		}

		return () => {
			for (const instanceId in instanceNodes) {
				removeInstance(instanceId);
			}
		};
	}, [instanceNodes]);

	// Unmount-only cleanup: remove instances from the session node.
	// The main instances effect cleanup deliberately keeps instances on the
	// node during cycle transitions (to avoid showing an empty scene), so
	// this separate effect (empty deps) handles actual unmount.
	useEffect(() => {
		return () => {
			Object.keys(speculativeProcessResolversRef.current).forEach(
				(token) => resolveSpeculativeProcess(Number(token)),
			);
			if (sessionNodeRef.current) {
				Object.values(instanceNodesRef.current).forEach((instance) => {
					if (sessionNodeRef.current!.hasChild(instance)) {
						sessionNodeRef.current!.removeChild(instance);
					}
				});
			}
		};
	}, [resolveSpeculativeProcess]);

	useEffect(() => {
		const removeSessionUpdateCallback = addSessionUpdateCallback(
			sessionApi?.id ?? "",
			sessionUpdateCallback,
		);

		return () => {
			removeSessionUpdateCallback();
		};
	}, [sessionApi, sessionUpdateCallback]);

	useEffect(() => {
		if (!sessionApi) return;

		let cancelled = false;
		let mainPromiseResolved = false;

		// create a promise to wait for all instances to be created
		// this is necessary to only resolve the process once all instances are created
		// and added to the controller session node
		let resolveMainPromise: () => void;
		const mainPromise = new Promise<void>((resolve) => {
			resolveMainPromise = resolve;
		});

		/**
		 * Safely resolve the main promise, ensuring it is only resolved once.
		 * This is called from the success path, the cancel paths, and the
		 * effect cleanup.
		 */
		const safeResolveMain = () => {
			if (mainPromiseResolved) return;
			mainPromiseResolved = true;
			resolveMainPromise!();
		};

		const mainProcessDefinition: IProcessDefinition = {
			name: "Instance Process",
			promise: mainPromise,
		};

		// Always create a new instances PM — do NOT reuse bridge PMs.
		// Bridge PMs stay alive until this PM has attached its own tracked work.
		const processManagerId = createProcessManager(sessionApi.id);
		addProcess(processManagerId, mainProcessDefinition);

		// Resolve only the bridge PMs that semantically belong to this effect
		// run. The new instances PM is already active, so flag coverage overlaps.
		onProcessManagerAttached?.(sessionProcessManagerId);
		const parsingProcessToken =
			parsingProcessTokensByParsedRevisionRef.current[
				parsedAppBuilderInstancesRevision
			];
		delete parsingProcessTokensByParsedRevisionRef.current[
			parsedAppBuilderInstancesRevision
		];
		resolveSpeculativeProcess(parsingProcessToken);
		Object.entries(sessionUpdateProcessTokensByVersionRef.current).forEach(
			([version, token]) => {
				if (Number(version) > sessionNodeVersion) return;
				delete sessionUpdateProcessTokensByVersionRef.current[
					Number(version)
				];
				resolveSpeculativeProcess(token);
			},
		);

		const newInstances: {
			[key: string]: {
				node: ITreeNode;
				definition: IParsedInstanceDefinition;
			};
		} = {};
		const customizationResultPromise: {
			[key: string]: Promise<ITreeNode>;
		} = {};
		const promises: Promise<void>[] = [];

		parsedAppBuilderInstances.forEach((instance) => {
			const promise = createInstance(
				instance,
				customizationResultPromise,
				newInstances,
				processManagerId,
				customizationResultInStoreRef.current,
				addCustomizationResult,
				addProcess,
			);
			promises.push(promise);
		});

		// wait for all instance promises to resolve
		// then look for output actions to be executed
		Promise.all(promises)
			.then(() => {
				if (cancelled) {
					safeResolveMain();
					return;
				}
				// Create a dedicated process manager for the scene tree update BEFORE
				// running processOutputActions. The controller session's customization
				// (triggered by batchParameterValueUpdate) creates its own process manager
				// which will remove SUSPEND_SCENE_UPDATES when it finishes. This PM
				// ensures flag protection spans the entire remaining pipeline.
				let resolveSceneTreePromise: () => void;
				const sceneTreePromise = new Promise<void>((resolve) => {
					resolveSceneTreePromise = resolve;
				});
				const sceneTreeProcessManagerId = createProcessManager(
					sessionApi.id,
				);
				addProcess(sceneTreeProcessManagerId, {
					name: "Scene Tree Update",
					promise: sceneTreePromise,
				});

				const outputCallbackPromises = processOutputActions(
					sessionApi,
					newInstances,
					namespace,
					batchParameterValueUpdate,
					getParameter,
					pendingOutputActionRequestKeyRef,
					pendingOutputActionPromiseRef,
				);

				// wait for all instance promises to resolve
				// before we add the instances to the session node
				Promise.all(outputCallbackPromises)
					.then(async () => {
						if (cancelled) {
							safeResolveMain();
							resolveSceneTreePromise!();
							return;
						}

						await addInstanceToSceneTree(
							newInstances,
							instanceNodesRef.current,
							sessionNodeRef.current,
						);

						// Register instances in the store synchronously before resolving
						// any promises. This ensures instances are present in the store
						// when SUSPEND_SCENE_UPDATES is removed and the scene update fires.
						const newInstanceNodes = Object.entries(
							newInstances,
						).reduce(
							(acc, [key, cur]) => {
								acc[key] = cur.node;
								return acc;
							},
							{} as {[key: string]: ITreeNode},
						);

						// Remove old instances from the store
						for (const instanceId in instanceNodesRef.current) {
							removeInstance(instanceId);
						}
						// Add new instances to the store
						for (const instanceId in newInstanceNodes) {
							addInstance(
								instanceId,
								newInstanceNodes[instanceId],
							);
						}
						instanceNodesRef.current = newInstanceNodes;

						// If the session node was replaced during processOutputActions
						// (controller customization), sessionNodeRef.current points to
						// the new node while addInstanceToSceneTree targeted the old one.
						// Reparent instances to the current session node.
						const currentSessionNode = sessionNodeRef.current;
						if (currentSessionNode) {
							Object.values(newInstanceNodes).forEach(
								(instance) => {
									if (
										!currentSessionNode.hasChild(instance)
									) {
										currentSessionNode.addChild(instance);
										currentSessionNode.updateVersion();
									}
								},
							);
						}

						// Update React state (for downstream consumers)
						setInstanceNodes(newInstanceNodes);

						// The scene-tree promise is resolved immediately since it only
						// needs to span the scene tree update itself.
						resolveSceneTreePromise!();

						// Resolve the main promise. Any follow-up work is covered by
						// its own semantic PM (output action/session update/cb/parse).
						safeResolveMain();

						// after the instances are added to the scene tree, we can adjust the cameras to fit the new geometry
						// we don't await this process, as it just waits for the next render loop
						adjustCamerasToInstances(firstLoadDoneRef);

						// clean up the session instances
						// only instances that are currently in the scene are kept
						// the others are removed from the store
						Object.keys(
							customizationResultInStoreRef.current,
						).forEach((instanceId) => {
							if (
								customizationResultPromise[instanceId] !==
								undefined
							)
								return;
							removeCustomizationResult(instanceId);
						});
					})
					.catch((error) => {
						Logger.error(
							"Failed to process output actions:",
							error,
						);
						safeResolveMain();
						resolveSceneTreePromise!();
					});
			})
			.catch((error) => {
				Logger.error("Failed to create instances:", error);
				safeResolveMain();
			});

		return () => {
			cancelled = true;
			// Resolve the main promise so this cycle's work is marked done.
			// Any next cycle has already created a semantic bridge PM before
			// this cleanup runs.
			safeResolveMain();

			// Do not remove instances from the session node or clear the ref
			// here. When a new cycle is starting (not unmount), the old
			// instances should stay on the node so the viewer doesn't show
			// an empty scene between cycles. The next cycle's
			// addInstanceToSceneTree will replace them, and
			// sessionUpdateCallback will re-add them to a replaced node.
			// The ref is only updated when new instances are built.
			setInstanceNodes({});
		};
	}, [
		parsedAppBuilderInstances,
		parsedAppBuilderInstancesRevision,
		namespace,
		onProcessManagerAttached,
		resolveSpeculativeProcess,
		sessionNodeVersion,
	]);
}

/**
 * Adds the new instances to the scene tree.
 * The last step before the process is finished.
 * Removes the old instances from the scene tree.
 *
 * @param newInstances
 * @param instances
 * @param sessionNode
 */
const addInstanceToSceneTree = async (
	newInstances: {
		[key: string]: {
			node: ITreeNode;
			definition: IParsedInstanceDefinition;
		};
	},
	instances: {[key: string]: ITreeNode},
	sessionNode?: ITreeNode,
) => {
	// we add the instances to the controller session node
	// and remove the old instances from the session node
	// this is necessary to happen before the process is finished
	if (sessionNode) {
		// remove the old instances from the session node
		Object.values(instances).forEach((instance) => {
			if (sessionNode!.hasChild(instance)) {
				sessionNode!.removeChild(instance);
			}
		});

		const promises: Promise<void>[] = [];

		Object.values(newInstances).forEach((instance) => {
			if (sessionNode!.hasChild(instance.node)) return;

			// if there is a material database, we need to apply it
			// we do this at the latest possible stage to avoid unnecessary calls to the material database
			// as the material database may be updated during the instance creation process
			if (GlobalAccessObjects.instance.assignMaterialFromDatabase) {
				promises.push(
					GlobalAccessObjects.instance.assignMaterialFromDatabase(
						instance.node,
					),
				);
			}

			// add the instance to the controller session node
			sessionNode!.addChild(instance.node);
			// update the version of the node
			// this won't be triggered as long as a process is running
			sessionNode!.updateVersion();
		});

		await Promise.all(promises);
	}
};

/**
 * We adjust the cameras to fit the new geometry of the instances.
 * This is necessary because the new instances may have geometry that is outside of the current camera view.
 * Do this either when:
 * - the instances are loaded for the first time and initialAutoAdjust is set on any viewport camera or viewport creation definition
 * - or when the instances are loaded and autoAdjust is set on any viewport camera
 *
 * @param firstLoadDoneRef
 */
const adjustCamerasToInstances = async (
	firstLoadDoneRef: React.MutableRefObject<boolean>,
) => {
	await new Promise((resolve) => setTimeout(resolve, 0));

	// After instances are loaded for the first time, check if initialAutoAdjust
	// is set on any viewport camera or viewport creation definition. If so,
	// trigger zoomTo because the controller session may have had no geometry
	// when the session first loaded.
	if (!firstLoadDoneRef.current) {
		firstLoadDoneRef.current = true;
		const {viewportAccessFunctions} =
			useShapeDiverStoreViewportAccessFunctions.getState();
		for (const viewportAccessFunction of Object.values(
			viewportAccessFunctions,
		)) {
			if (viewportAccessFunction.zoomTo) {
				if (viewportAccessFunction.dto?.initialAutoAdjust) {
					viewportAccessFunction.zoomTo(false, {
						duration: 0,
					});
				} else {
					viewportAccessFunction.zoomTo(true, {
						duration: 0,
					});
				}
			}
		}
	}

	// trigger a zoomTo on all viewports to adjust the camera to the new geometry
	// this is necessary because the new instances may have geometry that is outside of the current camera view
	// we do this after the instances are added to the session node to ensure that the geometry is in the scene when we trigger the zoomTo
	const {viewportAccessFunctions} =
		useShapeDiverStoreViewportAccessFunctions.getState();
	for (const viewportAccessFunction of Object.values(
		viewportAccessFunctions,
	)) {
		if (viewportAccessFunction.zoomTo) {
			viewportAccessFunction.zoomTo(true, {
				duration: 0,
			});
		}
	}
};

const stringifySessionParameterValue = (
	session: ISessionApi,
	parameterId: string,
	value: unknown,
) => {
	const parameter = session.parameters[parameterId];
	if (!parameter) return value + "";

	try {
		return parameter.stringify(value as never);
	} catch {
		return value + "";
	}
};

const parseFiniteNumber = (value: string) => {
	if (value.trim() === "") return undefined;
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : undefined;
};

const parameterValueMatchesCurrentSession = (
	session: ISessionApi,
	parameterId: string,
	value: string,
) => {
	const parameter = session.parameters[parameterId];
	const sessionValue =
		session.parameterValues[parameterId] ?? parameter?.value;

	if (sessionValue === undefined) return false;

	const requested = stringifySessionParameterValue(
		session,
		parameterId,
		value,
	);
	const current = stringifySessionParameterValue(
		session,
		parameterId,
		sessionValue,
	);

	if (requested === current) return true;

	if (requested.toLowerCase() === current.toLowerCase()) return true;

	const requestedNumber = parseFiniteNumber(requested);
	const currentNumber = parseFiniteNumber(current);
	if (requestedNumber !== undefined && currentNumber !== undefined) {
		return Math.abs(requestedNumber - currentNumber) < 1e-9;
	}

	return false;
};

const createEffectiveParameterValues = (
	session: ISessionApi,
	parameterValues: {[key: string]: string},
) => {
	const effectiveParameterValues: {[key: string]: string} = {};

	Object.keys(session.parameters).forEach((parameterId) => {
		const value =
			parameterValues[parameterId] ??
			session.parameterValues[parameterId] ??
			session.parameters[parameterId]?.value;

		if (value === undefined) return;

		effectiveParameterValues[parameterId] = stringifySessionParameterValue(
			session,
			parameterId,
			value,
		);
	});

	return effectiveParameterValues;
};

const createInstanceCustomizationId = (
	session: ISessionApi,
	parameterValues: {[key: string]: string},
) => {
	const effectiveParameterValues = createEffectiveParameterValues(
		session,
		parameterValues,
	);

	return `${session.id}_${JSON.stringify(
		Object.keys(effectiveParameterValues)
			.sort()
			.map((key) => [key, effectiveParameterValues[key]]),
	)}`;
};

const parameterValuesMatchCurrentSession = (
	session: ISessionApi,
	parameterValues: {[key: string]: string},
) =>
	Object.entries(parameterValues).every(([parameterId, value]) =>
		parameterValueMatchesCurrentSession(session, parameterId, value),
	);

/**
 * Creates an instance in the viewer.
 * The instance is created with the correct parameter set and transformations.
 * The instance creation is added to the process manager.
 *
 * @param instance
 * @param customizationResultPromise
 * @param newInstances
 * @param processManagerId
 * @param customizationResultInStore
 * @param addCustomizationResult
 * @param addProcess
 * @returns A promise that resolves once the instance is created and added to the newInstances object.
 */
const createInstance = (
	instance: IParsedInstanceDefinition,
	customizationResultPromise: {[key: string]: Promise<ITreeNode>},
	newInstances: {
		[key: string]: {
			node: ITreeNode;
			definition: IParsedInstanceDefinition;
		};
	},
	processManagerId: string,
	customizationResultInStore: {[key: string]: ITreeNode},
	addCustomizationResult: (instanceId: string, instance: ITreeNode) => void,
	addProcess: (processManagerId: string, process: IProcessDefinition) => void,
) => {
	// create a callback function for the progress
	let progressCallback: (progress: {
		percentage: number;
		msg?: string;
	}) => void;

	// create a function to register the progress callback
	const onProgressCallback = (
		callback: (progress: {percentage: number; msg?: string}) => void,
	) => {
		progressCallback = callback;
	};

	// The instance customization id is a combination of the session id and
	// a canonical representation of the effective parameter values (sparse
	// overrides merged with session defaults), resolved through the session
	// parameter API for consistent stringification.
	const instanceCustomizationId = createInstanceCustomizationId(
		instance.session,
		instance.parameterValues,
	);

	// the instance name is the name of the instance (if it exists, otherwise the original index)
	const instanceName =
		instance.name ?? `instances[${instance.originalIndex}]`;
	const currentSessionMatches = parameterValuesMatchCurrentSession(
		instance.session,
		instance.parameterValues,
	);

	// first we need to check if the session instance already exists
	// there are two cases:
	// 1. the instance has already been created in the last parameter update and is still in the store
	// 2. the instance has already been requested by another process and is currently awaited
	let creationPromise: Promise<ITreeNode>;
	if (customizationResultInStore[instanceCustomizationId]) {
		creationPromise = Promise.resolve(
			customizationResultInStore[instanceCustomizationId],
		);
		customizationResultPromise[instanceCustomizationId] = creationPromise;
	} else if (
		customizationResultPromise[instanceCustomizationId] !== undefined
	) {
		creationPromise = customizationResultPromise[instanceCustomizationId];
	} else if (currentSessionMatches) {
		creationPromise = Promise.resolve(instance.session.node).then(
			(node) => {
				addCustomizationResult(instanceCustomizationId, node);
				return node;
			},
		);
		customizationResultPromise[instanceCustomizationId] = creationPromise;
	} else {
		creationPromise = instance.session
			.customizeParallel(instance.parameterValues)
			.then((node) => {
				addCustomizationResult(instanceCustomizationId, node);
				return node;
			});
		customizationResultPromise[instanceCustomizationId] = creationPromise;
	}

	/**
	 * After the creation progress is finished, we need to add the transformations
	 * to the instance node.
	 * The transformations are added to the instance node as children.
	 * The instance node is then added to the controller session node.
	 */
	const promise = creationPromise.then(async (node) => {
		// send a progress update
		progressCallback({
			percentage: 0.45,
			msg: "Applying transformations to instance...",
		});
		const instanceNode = new TreeNode(instanceName);
		instanceNode.originalName = instanceName;

		// if no transformations are defined, we use the identity matrix
		const transformations = instance.transformations ?? [
			[1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1],
		];

		// add the session data to the instance node
		const sessionData = node.data.find((d) => d instanceof SessionData);
		if (sessionData && !instanceNode.hasData(sessionData))
			instanceNode.addData(sessionData);

		// once the node is created, add the transformations
		transformations.forEach((transformation, index) => {
			const transformationId = `transformations[${index}]`;
			const transformationNode = new TreeNode(transformationId);
			transformationNode.originalName = transformationId;

			// convert the transformation to a mat4
			const transformationMatrix = mat4.fromValues(
				...(transformation as unknown as Mat4Array),
			);

			transformationNode.addTransformation({
				id: transformationId,
				matrix: transformationMatrix,
			});

			for (let i = 0; i < node.children.length; i++) {
				const child = node.children[i];
				transformationNode.addChild(child.clone());
			}

			instanceNode.addChild(transformationNode);
		});

		// send a progress update
		progressCallback({
			percentage: 0.9,
			msg: "Adding instance to scene... ",
		});

		newInstances[instanceName] = {
			node: instanceNode,
			definition: instance,
		};
	});

	// add the promise to the process manager
	// once all registered promises are resolved, the viewports are updated
	// and the process manager is removed from the store
	const processDefinition: IProcessDefinition = {
		name: instanceName,
		promise: promise,
		onProgress: onProgressCallback,
	};
	addProcess(processManagerId, processDefinition);

	// call the first progress update
	progressCallback!({
		percentage: 0.1,
		msg: "Creating instance...",
	});

	return promise;
};

/**
 * Processes the output actions defined in the instance definition.
 * Currently, only the "setParameterValue" action is supported.
 *
 * @param sessionApi The session API of the controller session.
 * @param newInstances The newly created instances.
 * @param namespace The namespace of the app builder data.
 * @param batchParameterValueUpdate The function to batch update parameter values.
 * @returns An array of promises that resolve once the output actions are executed.
 */
const processOutputActions = (
	sessionApi: ISessionApi,
	newInstances: {
		[key: string]: {
			node: ITreeNode;
			definition: IParsedInstanceDefinition;
		};
	},
	namespace: string,
	batchParameterValueUpdate: (
		params: Record<string, any>,
		skipHistory?: boolean,
		skipUrlUpdate?: boolean,
	) => Promise<void>,
	getParameter: (
		namespace: string,
		key: string,
	) => IParameterStore | undefined,
	pendingOutputActionRequestKeyRef: MutableRefObject<string | undefined>,
	pendingOutputActionPromiseRef: MutableRefObject<Promise<void> | undefined>,
) => {
	const outputCallbackPromises: Promise<void>[] = [];

	// we gather all outputs to be set in one object
	// to only call the batch update once
	// this is necessary as multiple instances might want to set the same parameter
	const outputReturns: {
		// the parameter id
		[key: string]: {
			// first level, "instances[INDEX]" or name of the instance (if a name was provided)
			[key: string]: {
				// second level, the output displayname/name/id (same as specified in the outputActions)
				[key: string]: ResOutputContent[] | undefined;
			};
		};
	} = {};

	// if there are output actions defined, we need to execute them now
	Object.values(newInstances).forEach((instance) => {
		if (
			!instance.definition.outputActions ||
			instance.definition.outputActions.length === 0
		)
			return;

		// we currently only support setting parameter values
		// in the future, more actions can be added here
		instance.definition.outputActions.forEach((action) => {
			if (action.type === "setParameterValue") {
				// can be displayname, name or id
				const parameterIdentifier = action.props.parameter;

				const parameterId =
					sessionApi.getParameterByName(parameterIdentifier)[0]?.id ||
					sessionApi.getParameterById(parameterIdentifier)?.id;

				if (!parameterId) return;

				// can be displayname, name or id
				const outputIdentifier = action.props.output;

				// we only need the first child for this instance as the result
				// will be the same for all transformations
				const firstInstanceChild = instance.node?.children[0];
				if (!firstInstanceChild) return;

				let outputData: ResOutput | undefined;
				// we traverse the children which represent the computed outputs
				for (const child of firstInstanceChild.children) {
					const responseOutput = child.data.find(
						(d) => d instanceof SessionOutputData,
					)?.responseOutput;
					if (!responseOutput) continue;
					if (
						responseOutput.displayname === outputIdentifier ||
						responseOutput.name === outputIdentifier ||
						responseOutput.id === outputIdentifier
					) {
						outputData = responseOutput;
						break;
					}
				}

				if (outputData) {
					if (outputReturns[parameterId] !== undefined) {
						if (
							outputReturns[parameterId][
								instance.definition.name ??
									`instances[${instance.definition.originalIndex}]`
							] !== undefined
						) {
							outputReturns[parameterId][
								instance.definition.name ??
									`instances[${instance.definition.originalIndex}]`
							][outputIdentifier] = outputData.content;
						} else {
							outputReturns[parameterId][
								instance.definition.name ??
									`instances[${instance.definition.originalIndex}]`
							] = {
								[outputIdentifier]: outputData.content,
							};
						}
					} else {
						outputReturns[parameterId] = {
							[instance.definition.name ??
							`instances[${instance.definition.originalIndex}]`]:
								{
									[outputIdentifier]: outputData.content,
								},
						};
					}
				}
			}
		});
	});

	// now we check if there are any outputs to be set
	if (Object.keys(outputReturns).length > 0) {
		const param = Object.entries(outputReturns).reduce(
			(acc, [key, value]) => {
				const parameter = getParameter(namespace, key);
				if (!parameter) return acc;

				const nextValue = JSON.stringify(value);
				const currentValue = parameter.getState().state.execValue;
				if (currentValue === nextValue) {
					return acc;
				}

				acc[key] = nextValue;
				return acc;
			},
			{} as Record<string, any>,
		);

		const requestKey = JSON.stringify(param);

		// if there are no parameters to set, we skip the update call
		// this can happen if the output action wants to set a value
		// that is already set in the parameter
		if (Object.keys(param).length === 0) return outputCallbackPromises;

		if (pendingOutputActionRequestKeyRef.current === requestKey) {
			// The same request is already in flight from a previous cycle.
			// Don't start a duplicate batchParameterValueUpdate, but DO wait
			// for the pending one to complete. This keeps the process manager
			// alive until the customization actually finishes, preventing
			// premature flag removal and intermediate viewer results.
			if (pendingOutputActionPromiseRef.current) {
				outputCallbackPromises.push(
					pendingOutputActionPromiseRef.current,
				);
			}
			return outputCallbackPromises;
		}

		pendingOutputActionRequestKeyRef.current = requestKey;
		const updatePromise = batchParameterValueUpdate(
			{
				[namespace]: param,
			},
			true,
			true,
		).finally(() => {
			if (pendingOutputActionRequestKeyRef.current === requestKey) {
				pendingOutputActionRequestKeyRef.current = undefined;
				pendingOutputActionPromiseRef.current = undefined;
			}
		});
		pendingOutputActionPromiseRef.current = updatePromise;
		outputCallbackPromises.push(updatePromise);
	}

	return outputCallbackPromises;
};
